// services/pushNotifications.ts
//
// Web Push (FCM) client bootstrap.
//
// Footprint: the firebase/messaging SDK (~30 KB) is dynamic-imported here so
// it stays OUT of main.js — exactly like firebaseFunctions.ts does for the
// functions SDK. It only downloads when a user actually turns notifications
// on (or when a previously-opted-in user loads the app). First paint and the
// common player flow never pay for it.
//
// Storage (mirrored by functions/src/pushNotifications.ts):
//   /notifications/{uid}/push/enabled      -> boolean master toggle
//   /notifications/{uid}/push/tokens/{key} -> { token, ua, createdAt }
//
// The service worker (public/firebase-messaging-sw.js) handles background
// messages; foreground messages arrive via onForegroundMessage() below.

import type { Messaging, MessagePayload } from 'firebase/messaging';
import { ref, set, remove, serverTimestamp } from 'firebase/database';
import { app, db } from '../config/firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const SW_URL = `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js`;

// FCM tokens are per-device, but /notifications/{uid}/push/enabled is a single
// per-user flag. This localStorage marker records whether THIS specific
// browser/device has registered a token, so the UI can show an accurate
// per-device on/off state (and not claim "On" on a phone that never
// registered just because the desktop did). Cheap — no SDK load required.
const DEVICE_TOKEN_KEY = 'roundmaster_push_device_token';

function setDeviceMarker(token: string): void {
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    /* private mode / storage disabled — non-fatal */
  }
}

function clearDeviceMarker(): void {
  try {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** True if THIS device has registered for push (permission granted + token). */
export function isEnabledOnThisDevice(): boolean {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  try {
    return !!localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch {
    return false;
  }
}

/** True when the browser can actually do Web Push. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Current Notification permission ('default' | 'granted' | 'denied'). */
export function getPermission(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// FCM tokens contain characters ('/', etc.) that aren't valid RTDB keys.
// Derive a stable, lossy key — we never reverse it (the token value is stored
// inside the record), so collisions across distinct tokens are vanishingly
// unlikely and harmless.
function tokenKey(token: string): string {
  return token.replace(/[.#$/[\]]/g, '_');
}

let messagingPromise: Promise<Messaging | null> | null = null;

async function ensureMessaging(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const { getMessaging, isSupported } = await import('firebase/messaging');
      if (!(await isSupported())) return null;
      return getMessaging(app);
    })();
  }
  return messagingPromise;
}

/** Register (or reuse) the messaging service worker, passing the public config. */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  };
  const url = `${SW_URL}?${new URLSearchParams(config).toString()}`;
  // scope '/' so notificationclick can focus any app route.
  return navigator.serviceWorker.register(url, { scope: '/' });
}

export interface EnablePushResult {
  granted: boolean;
  reason?: 'unsupported' | 'denied' | 'no-vapid' | 'no-token';
  token?: string;
}

/**
 * Turn push on for a user: ask permission (if needed), register the SW, get an
 * FCM token, and persist it under /notifications/{uid}/push. Idempotent.
 */
export async function enablePush(uid: string): Promise<EnablePushResult> {
  if (!isPushSupported()) return { granted: false, reason: 'unsupported' };
  if (!VAPID_KEY) {
    // Misconfiguration (env var not set). Surface clearly rather than silently
    // appearing to work.
    console.error('[push] REACT_APP_FIREBASE_VAPID_KEY is not set — cannot enable push.');
    return { granted: false, reason: 'no-vapid' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { granted: false, reason: 'denied' };

  const registration = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  const messaging = await ensureMessaging();
  if (!messaging) return { granted: false, reason: 'unsupported' };

  const { getToken } = await import('firebase/messaging');
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) return { granted: false, reason: 'no-token' };

  await set(ref(db, `notifications/${uid}/push/tokens/${tokenKey(token)}`), {
    token,
    ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 256) : '',
    createdAt: serverTimestamp(),
  });
  await set(ref(db, `notifications/${uid}/push/enabled`), true);
  setDeviceMarker(token);

  return { granted: true, token };
}

/**
 * Turn push off: flip the master toggle, delete the FCM token, and remove it
 * from the database so this device stops receiving messages.
 */
export async function disablePush(uid: string): Promise<void> {
  // Per-device disable: drop THIS device's marker + token. Other devices keep
  // their own tokens (and keep receiving). Delivery is gated on token
  // presence, so removing the token is what stops this device.
  clearDeviceMarker();

  try {
    const messaging = await ensureMessaging();
    if (messaging && VAPID_KEY && isPushSupported()) {
      const { getToken, deleteToken } = await import('firebase/messaging');
      // Reuse the already-active SW (registered at enable time). Avoid passing
      // a clientURL to getRegistration — that can spawn a config-less default
      // SW. If there's no active registration there's nothing to clean up.
      const registration = await navigator.serviceWorker.getRegistration();
      const token = registration
        ? await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration,
          }).catch(() => null)
        : null;
      if (token) {
        await remove(ref(db, `notifications/${uid}/push/tokens/${tokenKey(token)}`));
        await deleteToken(messaging).catch(() => undefined);
      }
    }
  } catch (err) {
    // Best-effort cleanup; clearing the device marker already flips the UI off.
    console.warn('[push] disablePush cleanup failed:', err);
  }
}

/**
 * Get THIS device's current FCM token without persisting anything. Used by the
 * test endpoint to target only this device. Returns null if push isn't set up
 * on this device.
 */
export async function getCurrentToken(): Promise<string | null> {
  if (!isPushSupported() || !VAPID_KEY || Notification.permission !== 'granted') return null;
  try {
    const registration = await registerServiceWorker();
    await navigator.serviceWorker.ready;
    const messaging = await ensureMessaging();
    if (!messaging) return null;
    const { getToken } = await import('firebase/messaging');
    return await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch {
    return null;
  }
}

/**
 * Show a notification straight from the service worker — no server, no FCM.
 * Confirms whether THIS device can display notifications at all (permission +
 * SW), isolating display problems from FCM delivery problems.
 */
export async function showLocalTestNotification(): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  try {
    const registration =
      (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
    await navigator.serviceWorker.ready;
    await registration.showNotification('RoundMaster — local test', {
      body: 'If you can see this, this device can display notifications.',
      icon: '/logo192.png',
      badge: '/favicon-48x48.png',
      tag: 'roundmaster-local-test',
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Render a real OS notification via the service worker — used to surface a push
 * even while the tab is FOCUSED (FCM delivers foreground messages to the page,
 * not the SW, so without this a focused user would only get an in-app toast and
 * could miss it). Mirrors the presentation of the background SW handler.
 */
export async function showSystemNotification(data: {
  title: string;
  body: string;
  link: string;
  type: string;
  tag?: string;
}): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  try {
    const registration =
      (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
    await navigator.serviceWorker.ready;
    await registration.showNotification(data.title || 'RoundMaster', {
      body: data.body || '',
      icon: '/logo192.png',
      badge: '/favicon-48x48.png',
      tag: data.tag || undefined,
      // Re-alert even when one with the same tag is already on screen.
      // `renotify` is valid at runtime but missing from the lib DOM types.
      renotify: !!data.tag,
      data: { link: data.link || '/', type: data.type || '' },
    } as NotificationOptions & { renotify?: boolean })
  } catch (err) {
    console.warn('[push] showSystemNotification failed:', err);
  }
}

/**
 * Refresh this device's token for an already-enabled user (call on app load).
 * Keeps the stored token current as FCM rotates it. No permission prompt.
 */
export async function refreshToken(uid: string): Promise<void> {
  if (!isPushSupported() || !VAPID_KEY || Notification.permission !== 'granted') return;
  try {
    const registration = await registerServiceWorker();
    await navigator.serviceWorker.ready;
    const messaging = await ensureMessaging();
    if (!messaging) return;
    const { getToken } = await import('firebase/messaging');
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      await set(ref(db, `notifications/${uid}/push/tokens/${tokenKey(token)}`), {
        token,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 256) : '',
        createdAt: serverTimestamp(),
      });
      setDeviceMarker(token);
    }
  } catch (err) {
    console.warn('[push] refreshToken failed:', err);
  }
}

/**
 * Subscribe to foreground messages (tab focused). Returns an unsubscribe fn.
 * Messages are data-only; the callback receives { title, body, link, type }.
 */
export async function onForegroundMessage(
  cb: (data: { title: string; body: string; link: string; type: string; tag: string }) => void,
): Promise<() => void> {
  const messaging = await ensureMessaging();
  if (!messaging) return () => undefined;
  const { onMessage } = await import('firebase/messaging');
  return onMessage(messaging, (payload: MessagePayload) => {
    const data = payload.data || {};
    const parsed = {
      title: data.title || 'RoundMaster',
      body: data.body || '',
      link: data.link || '/',
      type: data.type || '',
      tag: data.tag || '',
    };
    // Always raise a real OS notification, even though the tab is focused, so a
    // foreground user can't miss it (matches the background SW behaviour). The
    // in-app toast (via cb) stays as a lighter, second reinforcement.
    void showSystemNotification(parsed);
    cb(parsed);
  });
}

// ============================================================================
// Per-tournament follower index — players "following" a tournament who opted
// into push get the "next round is being paired" notification.
// Staff are resolved server-side from their assignment, so they don't need
// this. Writes /tournaments/{tid}/pushFollowers/{uid}.
// ============================================================================

export async function registerFollower(tournamentId: string, uid: string): Promise<void> {
  try {
    await set(ref(db, `tournaments/${tournamentId}/pushFollowers/${uid}`), true);
  } catch (err) {
    console.warn('[push] registerFollower failed:', err);
  }
}

export async function unregisterFollower(tournamentId: string, uid: string): Promise<void> {
  try {
    await remove(ref(db, `tournaments/${tournamentId}/pushFollowers/${uid}`));
  } catch (err) {
    /* non-fatal */
  }
}
